/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import {ErrorType, ResponseData} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {ActivateHandle} from '../../../../util/interfaces';
import {applyCoupon} from '../../../../util/requestSender';
import {createErrorMessage} from '../../../../util/utils';
import SubscriptionCoupon from './subscriptionCoupon';
import {onShowConfirmSnackbar} from '../../../../util/renderUtil';
import {AppContext} from '../../../misc/appContext';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: VoidFunction;
}

function SubscriptionCouponController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const {onSuccess} = props;
    const [active, setActive] = useState(false);
    const [coupon, setCoupon] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType>(null);

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setCoupon('');
        setLoading(false);
        setError(null);
    }

    function onChangeCoupon(value: string) {
        setCoupon(value);
    }

    function onSubmit() {
        if (!account) return;
        setError(null);
        setLoading(true);
        applyCoupon({
            data: {account_id: account.id, coupon},
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    onSuccess();
                    onClose();
                    onShowConfirmSnackbar(
                        t('settings.subscription.couponApplied', 'De coupon is toegepast op jouw abonnement')
                    );
                } else if (data.message) setError(data.message);
                else
                    showDialog(
                        createErrorMessage(
                            t('settings.subscription.couponFailedMsg', 'Het toepassen van de coupon is mislukt.')
                        ),
                        t('settings.subscription.couponFailedTitle', 'Toepassen mislukt')
                    );
            }
        });
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <SubscriptionCoupon
            onClose={onClose}
            onChangeCoupon={onChangeCoupon}
            onSubmit={onSubmit}
            active={active}
            loading={loading}
            error={error}
            coupon={coupon}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(SubscriptionCouponController);
